/**
 * Operational records for the Community workspace — what a society actually
 * has to run, as opposed to what a student sees of it on /social.
 *
 * These are demonstration records. `isDemo` is on the export so no screen can
 * present them as a Campuzon claim about a real society.
 */

export const isDemo = true

export interface CommunityProfile {
  id: string
  name: string
  kind: string
  institution: string
  campus: string
  recognised: boolean
  recognitionNote: string
  members: number
  followers: number
  leadName: string
  leadRole: string
}

export const communityProfile: CommunityProfile = {
  id: 'org-eng-society',
  name: 'Engineering Society',
  kind: 'Student society',
  institution: 'Federal University of Technology, Akure',
  campus: 'FUTA Main Campus',
  recognised: true,
  recognitionNote:
    'Checked against the institution’s register of recognised societies on 4 March. Recognition says the society exists officially — it is not a statement about any individual member.',
  members: 1240,
  followers: 3180,
  leadName: 'Amarachi Obi',
  leadRole: 'Community manager',
}

/* ------------------------------------------------------------------ members */

export type MemberState = 'active' | 'pending' | 'invited' | 'suspended'

export interface CommunityMember {
  id: string
  name: string
  handle: string
  role: string
  state: MemberState
  joined: string
  programme: string
  standing: string
  lastActive: string
}

export const communityMembers: CommunityMember[] = [
  {
    id: 'mem-1',
    name: 'Ifeoma Eze',
    handle: '@ifeoma.e',
    role: 'Events manager',
    state: 'active',
    joined: '12 Oct 2024',
    programme: 'B.Eng. Mechanical Engineering',
    standing: 'Verified student',
    lastActive: 'Today',
  },
  {
    id: 'mem-2',
    name: 'Tunde Balogun',
    handle: '@tunde.b',
    role: 'Content manager',
    state: 'active',
    joined: '3 Nov 2024',
    programme: 'B.Sc. Computer Science',
    standing: 'Verified student',
    lastActive: 'Yesterday',
  },
  {
    id: 'mem-3',
    name: 'Blessing Okoro',
    handle: '@blessing.o',
    role: 'Moderator',
    state: 'active',
    joined: '20 Jan 2025',
    programme: 'B.Eng. Civil Engineering',
    standing: 'Institutional email verified',
    lastActive: '3 days ago',
  },
  {
    id: 'mem-4',
    name: 'Samuel Adeyemi',
    handle: '@sam.ade',
    role: 'Member',
    state: 'pending',
    joined: 'Requested 2 days ago',
    programme: 'B.Eng. Electrical Engineering',
    standing: 'Verified student',
    lastActive: '2 days ago',
  },
  {
    id: 'mem-5',
    name: 'Ngozi Umeh',
    handle: '@ngozi.u',
    role: 'Treasurer',
    state: 'invited',
    joined: 'Invited 5 days ago',
    programme: 'B.Sc. Statistics',
    standing: 'No academic record checked',
    lastActive: '—',
  },
]

export const memberRoles = [
  'Community manager',
  'Events manager',
  'Content manager',
  'Moderator',
  'Treasurer',
  'Member',
]

/* -------------------------------------------------------------------- posts */

export type PostState = 'published' | 'scheduled' | 'draft' | 'needs review'

export interface CommunityPost {
  id: string
  author: string
  excerpt: string
  state: PostState
  audience: string
  when: string
  reactions: number
  comments: number
  flagged?: string
}

export const communityPosts: CommunityPost[] = [
  {
    id: 'post-1',
    author: '@ifeoma.e',
    excerpt:
      'Engineering Week schedule is confirmed. Sessions run Monday to Thursday in the CET auditorium — full timetable on the events page.',
    state: 'published',
    audience: 'Community members',
    when: '2 hours ago',
    reactions: 84,
    comments: 12,
  },
  {
    id: 'post-2',
    author: '@tunde.b',
    excerpt:
      'Looking for two volunteers to run the registration desk on Wednesday morning. Reply here if you can take a shift.',
    state: 'published',
    audience: 'Community members',
    when: 'Yesterday',
    reactions: 31,
    comments: 9,
  },
  {
    id: 'post-3',
    author: '@sam.ade',
    excerpt:
      'Selling a barely used drafting set — DM me for price. Also taking orders for printed past questions.',
    state: 'needs review',
    audience: 'Community members',
    when: '4 hours ago',
    reactions: 3,
    comments: 1,
    flagged: 'Reported as trading in a non-marketplace space. Marketplace listings belong in Market.',
  },
  {
    id: 'post-4',
    author: '@amarachi.o',
    excerpt: 'Career Talk with three alumni engineers — Friday 4pm. Registration opens Wednesday.',
    state: 'scheduled',
    audience: 'Campus',
    when: 'Publishes Wednesday, 08:00',
    reactions: 0,
    comments: 0,
  },
]

/* ------------------------------------------------------------ announcements */

export interface Announcement {
  id: string
  title: string
  body: string
  audience: string
  sent: string
  reach: number
  opened: number
}

export const announcements: Announcement[] = [
  {
    id: 'ann-1',
    title: 'Engineering Week starts Monday',
    body: 'Four days of talks, a build competition and the alumni dinner. Registration is required for the dinner only.',
    audience: 'All members',
    sent: '2 days ago',
    reach: 1240,
    opened: 902,
  },
  {
    id: 'ann-2',
    title: 'Annual general meeting — nominations open',
    body: 'Nominations for next session’s executive close on the 28th. Any member in good standing may stand.',
    audience: 'All members',
    sent: '2 weeks ago',
    reach: 1240,
    opened: 741,
  },
]

/* ------------------------------------------------------------------- events */

export interface CommunityEvent {
  id: string
  title: string
  image: string
  date: string
  venue: string
  capacity: number
  registered: number
  checkedIn: number | null
  state: 'open' | 'full' | 'closed' | 'draft'
  ticket: string
}

export const communityEvents: CommunityEvent[] = [
  {
    id: 'evt-1',
    title: 'Engineering Week — opening keynote',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/24fd2cdb-1b39-40a3-90dc-3f5db4d5f56c.jpg',
    date: 'Mon 24 Aug · 10:00',
    venue: 'CET Auditorium',
    capacity: 400,
    registered: 356,
    checkedIn: null,
    state: 'open',
    ticket: 'Free',
  },
  {
    id: 'evt-2',
    title: 'Alumni career talk',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/1c6a03e5-72cd-4c2b-83f2-0a1f0c4a5e4f.jpg',
    date: 'Fri 28 Aug · 16:00',
    venue: 'Lecture Theatre 2',
    capacity: 120,
    registered: 120,
    checkedIn: null,
    state: 'full',
    ticket: 'Free',
  },
  {
    id: 'evt-3',
    title: 'Technical workshop — embedded systems',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/8b7d4c9e-2f61-4a3b-9c5e-7d1e6a2b4f83.jpg',
    date: 'Sat 5 Sep · 09:00',
    venue: 'Engineering Lab B',
    capacity: 40,
    registered: 0,
    checkedIn: null,
    state: 'draft',
    ticket: '₦2,000',
  },
]

/* ------------------------------------------------------------ registrations */

export interface Registration {
  id: string
  event: string
  name: string
  handle: string
  when: string
  status: 'confirmed' | 'waitlisted' | 'cancelled'
  paid: string
}

export const registrations: Registration[] = [
  {
    id: 'reg-1',
    event: 'Engineering Week — opening keynote',
    name: 'Blessing Okoro',
    handle: '@blessing.o',
    when: '3 hours ago',
    status: 'confirmed',
    paid: 'Free',
  },
  {
    id: 'reg-2',
    event: 'Alumni career talk',
    name: 'Samuel Adeyemi',
    handle: '@sam.ade',
    when: 'Yesterday',
    status: 'waitlisted',
    paid: 'Free',
  },
  {
    id: 'reg-3',
    event: 'Engineering Week — opening keynote',
    name: 'Ngozi Umeh',
    handle: '@ngozi.u',
    when: '2 days ago',
    status: 'cancelled',
    paid: 'Free',
  },
]

/* -------------------------------------------------------------------- polls */

export interface Poll {
  id: string
  question: string
  state: 'open' | 'closed'
  anonymous: boolean
  closes: string
  votes: number
  options: { label: string; count: number }[]
}

export const polls: Poll[] = [
  {
    id: 'poll-1',
    question: 'Which day suits the technical workshop best?',
    state: 'open',
    anonymous: false,
    closes: 'Closes in 2 days',
    votes: 218,
    options: [
      { label: 'Saturday morning', count: 128 },
      { label: 'Saturday afternoon', count: 54 },
      { label: 'Sunday afternoon', count: 36 },
    ],
  },
  {
    id: 'poll-2',
    question: 'Should the alumni dinner stay ticketed?',
    state: 'closed',
    anonymous: true,
    closes: 'Closed 6 days ago',
    votes: 407,
    options: [
      { label: 'Yes — it covers the venue', count: 291 },
      { label: 'No — find sponsorship', count: 116 },
    ],
  },
]

/* ---------------------------------------------------------------- analytics */

export interface CommunityMetric {
  label: string
  value: string
  note: string
}

export const communityMetrics: CommunityMetric[] = [
  {
    label: 'Members',
    value: '1,240',
    note: 'People who joined. Following is counted separately, because many follow without joining.',
  },
  {
    label: 'Confirmed attendance',
    value: '312',
    note: 'People who actually checked in over the last term — not registrations, which are always higher.',
  },
  {
    label: 'Posts answered',
    value: '68%',
    note: 'Questions the asker marked resolved. Unanswered questions are the ones worth chasing.',
  },
  {
    label: 'Median reply time',
    value: '4h 20m',
    note: 'How long a member waits for the first reply from anyone in the society.',
  },
]
